import {
  ArrowUpRight,
  Bot,
  Check,
  CheckSquare2,
  ChevronDown,
  CircleHelp,
  Copy,
  KeyRound,
  LoaderCircle,
  Plus,
  Sparkles,
  Square,
  X
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAgentProfiles,
  getAgentWorkPlans,
  listWorkJourneys,
  saveAgentProfile,
  startAgentWorkPlan,
  updateAgentWorkPlan,
  type AgentProfileRow,
  type AgentWorkPlanRow,
  type WorkJourneyItemRow
} from '../api';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';

type Tab = 'work' | 'profiles';
type RequestError = {
  response?: { data?: { message?: string } };
  message?: string;
};
const exampleInstructions =
  'Turn the selected task into a short, practical plan. Identify the first useful step, likely files or teams involved only when the task says so, and questions that must be answered before work begins. Do not invent acceptance criteria.';

function failureMessage(error: unknown) {
  const candidate = error as RequestError;
  return (
    candidate.response?.data?.message ||
    candidate.message ||
    'Cindro could not complete that request.'
  );
}

function taskIdentifier(task: WorkJourneyItemRow) {
  const metadata = task.metadata || {};
  return String(
    metadata['identifier'] || metadata['customId'] || task.externalRefId
  );
}

function taskOwner(task: WorkJourneyItemRow) {
  const assignees = task.metadata?.['assignees'];
  if (!Array.isArray(assignees) || !assignees.length) return 'Unassigned';
  return assignees
    .map((value) =>
      typeof value === 'string'
        ? value
        : value && typeof value === 'object'
          ? String(
              (value as Record<string, unknown>)['name'] ||
                (value as Record<string, unknown>)['email'] ||
                'Assignee'
            )
          : ''
    )
    .filter(Boolean)
    .join(', ');
}

export function AgentWorkPanel() {
  const { session } = useSession();
  const { activeOrganization } = useWorkspace();
  const { toast } = useFeedback();
  const organizationId = activeOrganization?.id || '';
  const canManage =
    session?.user?.role === 'owner' ||
    session?.user?.role === 'admin' ||
    session?.user?.isSuperAdmin === true;
  const [tab, setTab] = useState<Tab>('work');
  const [profiles, setProfiles] = useState<AgentProfileRow[]>([]);
  const [journeys, setJourneys] = useState<
    Awaited<ReturnType<typeof listWorkJourneys>>
  >([]);
  const [plans, setPlans] = useState<AgentWorkPlanRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [taskQuery, setTaskQuery] = useState('');
  const [taskSource, setTaskSource] = useState<'all' | 'clickup' | 'plane'>(
    'all'
  );
  const [profileId, setProfileId] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<AgentProfileRow | null>(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState<AgentProfileRow['role']>('engineering');
  const [provider, setProvider] =
    useState<AgentProfileRow['modelProvider']>('openai');
  const [model, setModel] = useState('');
  const [instructions, setInstructions] = useState(exampleInstructions);
  const [taskProviders, setTaskProviders] = useState<string[]>([
    'clickup',
    'plane'
  ]);
  const [apiKey, setApiKey] = useState('');

  const load = useCallback(async () => {
    if (!session?.accessToken || !organizationId) return;
    setLoading(true);
    try {
      const [loadedProfiles, loadedPlans, loadedJourneys] = await Promise.all([
        getAgentProfiles(session.accessToken, organizationId),
        getAgentWorkPlans(session.accessToken, organizationId),
        listWorkJourneys(session.accessToken, { organizationId })
      ]);
      setProfiles(loadedProfiles);
      setPlans(loadedPlans);
      setJourneys(loadedJourneys);
      setProfileId((current) =>
        loadedProfiles.some(
          (profile) =>
            profile.agentProfileId === current && profile.status === 'active'
        )
          ? current
          : loadedProfiles.find((profile) => profile.status === 'active')
              ?.agentProfileId || ''
      );
      setError('');
    } catch (failure: unknown) {
      setError(failureMessage(failure));
    } finally {
      setLoading(false);
    }
  }, [organizationId, session?.accessToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const tasks = useMemo(
    () =>
      journeys
        .flatMap((journey) => journey.items)
        .filter(
          (item) =>
            item.kind === 'task' &&
            ['clickup', 'plane'].includes(item.provider) &&
            !['closed', 'success'].includes(item.status)
        ),
    [journeys]
  );
  const selectedTasks = useMemo(
    () =>
      selectedIds
        .map((id) => tasks.find((task) => task.workJourneyItemId === id))
        .filter((task): task is WorkJourneyItemRow => Boolean(task)),
    [selectedIds, tasks]
  );
  const visibleTasks = useMemo(() => {
    const query = taskQuery.trim().toLocaleLowerCase();
    return tasks.filter(
      (task) =>
        (taskSource === 'all' || task.provider === taskSource) &&
        (!query ||
          `${task.title} ${taskIdentifier(task)} ${taskOwner(task)}`
            .toLocaleLowerCase()
            .includes(query))
    );
  }, [taskQuery, taskSource, tasks]);
  const selectedProviders = useMemo(
    () => [...new Set(selectedTasks.map((task) => task.provider))],
    [selectedTasks]
  );
  const availableProfiles = profiles.filter(
    (profile) =>
      profile.status === 'active' &&
      selectedProviders.every((source) =>
        profile.taskProviders.includes(source)
      )
  );
  const chosenProfile = profiles.find(
    (profile) => profile.agentProfileId === profileId
  );

  useEffect(() => {
    if (
      profileId &&
      !availableProfiles.some((profile) => profile.agentProfileId === profileId)
    )
      setProfileId(availableProfiles[0]?.agentProfileId || '');
  }, [availableProfiles, profileId]);

  function toggleTask(taskId: string) {
    setSelectedIds((current) =>
      current.includes(taskId)
        ? current.filter((id) => id !== taskId)
        : current.length < 8
          ? [...current, taskId]
          : current
    );
  }

  function beginProfile(profile?: AgentProfileRow) {
    setEditing(profile || null);
    setName(profile?.name || '');
    setRole(profile?.role || 'engineering');
    setProvider(profile?.modelProvider || 'openai');
    setModel(profile?.model || '');
    setInstructions(profile?.instructions || exampleInstructions);
    setTaskProviders(profile?.taskProviders || ['clickup', 'plane']);
    setApiKey('');
  }

  async function saveProfile(event: React.FormEvent) {
    event.preventDefault();
    if (!session?.accessToken || !organizationId) return;
    setBusy(true);
    try {
      await saveAgentProfile(session.accessToken, {
        organizationId,
        agentProfileId: editing?.agentProfileId,
        name: name.trim(),
        role,
        instructions: instructions.trim(),
        modelProvider: provider,
        model: model.trim(),
        taskProviders,
        apiKey: apiKey.trim() || undefined,
        status: editing?.status || 'active'
      });
      toast(
        'success',
        editing ? 'Agent profile updated' : 'Agent profile created'
      );
      beginProfile();
      await load();
    } catch (failure: unknown) {
      toast('error', failureMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  async function savePlan() {
    if (
      !session?.accessToken ||
      !organizationId ||
      !profileId ||
      !selectedIds.length
    )
      return;
    setBusy(true);
    try {
      const plan = await startAgentWorkPlan(session.accessToken, {
        organizationId,
        agentProfileId: profileId,
        taskItemIds: selectedIds,
        additionalContext: additionalContext.trim() || undefined
      });
      setPlans((current) => [plan, ...current]);
      setSelectedIds([]);
      setAdditionalContext('');
      toast('success', 'Draft plan is ready for review');
    } catch (failure: unknown) {
      toast('error', failureMessage(failure));
    } finally {
      setBusy(false);
    }
  }

  async function updatePlan(planId: string, status: 'accepted' | 'stopped') {
    if (!session?.accessToken) return;
    try {
      const updated = await updateAgentWorkPlan(
        session.accessToken,
        planId,
        status
      );
      setPlans((current) =>
        current.map((plan) =>
          plan.agentWorkPlanId === planId ? updated : plan
        )
      );
      toast(
        'success',
        status === 'accepted'
          ? 'Plan accepted. No work has been dispatched.'
          : 'Plan stopped'
      );
    } catch (failure: unknown) {
      toast('error', failureMessage(failure));
    }
  }

  async function copyPlan(plan: AgentWorkPlanRow) {
    const content = [
      plan.plan.summary,
      ...plan.plan.steps.map(
        (step, index) => `${index + 1}. ${step.title}\n   ${step.detail}`
      ),
      ...(plan.plan.questions.length
        ? [`Questions: ${plan.plan.questions.join('; ')}`]
        : [])
    ].join('\n\n');
    try {
      await navigator.clipboard.writeText(content);
      toast('success', 'Plan copied');
    } catch {
      toast('error', 'Could not copy the plan');
    }
  }

  return (
    <section className="agentWorkPage">
      <div className="agentWorkHeader">
        <div>
          <div className="sectionEyebrow">TEAM WORK</div>
          <h2>Turn a task into a clear next step</h2>
          <p>
            Choose one or more ClickUp or Plane tasks. A configured role drafts
            a plan for a person to review.
          </p>
        </div>
        <div
          className="agentWorkTabs"
          role="tablist"
          aria-label="Agent work sections"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'work'}
            className={tab === 'work' ? 'isActive' : ''}
            onClick={() => setTab('work')}
          >
            Plan work
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'profiles'}
            className={tab === 'profiles' ? 'isActive' : ''}
            onClick={() => setTab('profiles')}
          >
            Agent profiles <span>{profiles.length}</span>
          </button>
        </div>
      </div>

      {error ? (
        <div className="agentWorkNotice agentWorkNotice--error" role="alert">
          <CircleHelp size={17} />
          <span>{error}</span>
          <button type="button" onClick={() => void load()}>
            Try again
          </button>
        </div>
      ) : null}
      {loading ? (
        <div className="agentWorkLoading">
          <LoaderCircle className="spin" size={20} /> Loading tasks and
          profiles…
        </div>
      ) : null}

      {!loading && tab === 'work' ? (
        <div className="agentWorkLayout">
          <section className="agentWorkCompose">
            <header className="agentWorkSectionHeading">
              <div>
                <div className="sectionEyebrow">START WITH THE WORK</div>
                <h3>Choose tasks</h3>
                <p>
                  Pick up to eight tasks. Tasks stay linked to their original
                  tracker.
                </p>
              </div>
              <span className="agentWorkCount">
                {selectedIds.length}/8 selected
              </span>
            </header>
            {tasks.length ? (
              <>
                <div className="agentTaskFilters">
                  <label className="agentTaskSearch">
                    Search tasks
                    <input
                      type="search"
                      value={taskQuery}
                      onChange={(event) => setTaskQuery(event.target.value)}
                      placeholder="Title, ID or assignee"
                    />
                  </label>
                  <label className="agentTaskSourceFilter">
                    Source
                    <select
                      value={taskSource}
                      onChange={(event) =>
                        setTaskSource(event.target.value as typeof taskSource)
                      }
                    >
                      <option value="all">All trackers</option>
                      <option value="clickup">ClickUp</option>
                      <option value="plane">Plane</option>
                    </select>
                  </label>
                </div>
                <div className="agentTaskList">
                  {visibleTasks.map((task) => (
                    <div
                      className="agentTaskOption"
                      key={task.workJourneyItemId}
                    >
                      <button
                        type="button"
                        className={`agentTaskChoice${selectedIds.includes(task.workJourneyItemId) ? ' isSelected' : ''}`}
                        onClick={() => toggleTask(task.workJourneyItemId)}
                        aria-pressed={selectedIds.includes(
                          task.workJourneyItemId
                        )}
                      >
                        {selectedIds.includes(task.workJourneyItemId) ? (
                          <CheckSquare2 size={19} />
                        ) : (
                          <Square size={19} />
                        )}
                        <span className="agentTaskChoiceMain">
                          <span className="agentTaskTitle">{task.title}</span>
                          <span className="agentTaskMeta">
                            <b>
                              {task.provider === 'clickup'
                                ? 'ClickUp'
                                : 'Plane'}
                            </b>
                            <span>{taskIdentifier(task)}</span>
                            <span>{taskOwner(task)}</span>
                          </span>
                        </span>
                        <span
                          className={`agentTaskStatus agentTaskStatus--${task.status}`}
                        >
                          {task.status.replace('_', ' ')}
                        </span>
                      </button>
                      {task.url ? (
                        <a
                          className="agentTaskLink"
                          aria-label={`Open ${task.title} in ${task.provider}`}
                          href={task.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ArrowUpRight size={15} />
                        </a>
                      ) : null}
                    </div>
                  ))}
                  {!visibleTasks.length ? (
                    <p className="agentTaskNoResults">
                      No tasks match these filters.
                    </p>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="agentWorkEmpty">
                <Bot size={24} />
                <h4>No open tasks are connected yet</h4>
                <p>
                  Connect ClickUp or Plane in Connections, then sync a list or
                  project to bring tasks here.
                </p>
              </div>
            )}

            <div className="agentWorkFormRow">
              <label htmlFor="agent-profile">Plan with</label>
              <select
                id="agent-profile"
                value={profileId}
                onChange={(event) => setProfileId(event.target.value)}
                disabled={!availableProfiles.length}
              >
                <option value="">
                  {selectedIds.length
                    ? 'No matching active profile'
                    : 'Choose tasks first'}
                </option>
                {availableProfiles.map((profile) => (
                  <option
                    key={profile.agentProfileId}
                    value={profile.agentProfileId}
                  >
                    {profile.name} · {profile.modelProvider} / {profile.model}
                  </option>
                ))}
              </select>
            </div>
            <label className="agentContextLabel" htmlFor="agent-context">
              What should the agent know? <span>Optional</span>
            </label>
            <textarea
              id="agent-context"
              rows={3}
              maxLength={8000}
              value={additionalContext}
              onChange={(event) => setAdditionalContext(event.target.value)}
              placeholder="Add relevant background or constraints. Don’t include secrets."
            />
            <div className="agentDataDisclosure">
              <KeyRound size={17} />
              <p>
                {chosenProfile ? (
                  <>
                    Selected task titles, tracker IDs, statuses, assignees,
                    links
                    {additionalContext.trim() ? ' and your added context' : ''}{' '}
                    will be sent to{' '}
                    <strong>
                      {chosenProfile.modelProvider === 'openai'
                        ? 'OpenAI'
                        : 'Anthropic'}{' '}
                      ({chosenProfile.model})
                    </strong>{' '}
                    to draft this plan. Cindro won’t change tasks or run code.
                  </>
                ) : (
                  'Choose tasks and an agent profile to see exactly what will be sent before you start.'
                )}
              </p>
            </div>
            <button
              type="button"
              className="primaryButton agentPlanButton"
              disabled={busy || !selectedIds.length || !profileId}
              onClick={() => void savePlan()}
            >
              {busy ? (
                <LoaderCircle className="spin" size={17} />
              ) : (
                <Sparkles size={17} />
              )}{' '}
              Draft plan for {selectedIds.length || 'selected'} task
              {selectedIds.length === 1 ? '' : 's'}
            </button>
          </section>

          <section className="agentPlanList">
            <header className="agentWorkSectionHeading">
              <div>
                <div className="sectionEyebrow">HUMAN REVIEW</div>
                <h3>Recent plans</h3>
                <p>
                  Accepting a plan records your decision; it does not start a
                  runner.
                </p>
              </div>
            </header>
            {plans.length ? (
              <div className="agentSavedPlans">
                {plans.map((plan) => (
                  <article
                    className="agentSavedPlan"
                    key={plan.agentWorkPlanId}
                  >
                    <div className="agentSavedPlanTop">
                      <span
                        className={`agentPlanState agentPlanState--${plan.status}`}
                      >
                        {plan.status === 'draft'
                          ? 'Needs review'
                          : plan.status === 'accepted'
                            ? 'Accepted'
                            : 'Stopped'}
                      </span>
                      <span>
                        {plan.profileSnapshot.name} ·{' '}
                        {plan.profileSnapshot.modelProvider} /{' '}
                        {plan.profileSnapshot.model}
                      </span>
                      <time>
                        {plan.created
                          ? new Date(plan.created).toLocaleString()
                          : ''}
                      </time>
                    </div>
                    <h4>{plan.plan.summary}</h4>
                    <div className="agentPlanSources">
                      {plan.sourceTasks.map((task) => (
                        <a
                          href={task.url || '#'}
                          key={task.workJourneyItemId}
                          target={task.url ? '_blank' : undefined}
                          rel={task.url ? 'noreferrer' : undefined}
                        >
                          {task.provider} · {task.displayId}: {task.title}
                          {task.url ? <ArrowUpRight size={13} /> : null}
                        </a>
                      ))}
                    </div>
                    <ol className="agentPlanSteps">
                      {plan.plan.steps.map((step, index) => (
                        <li key={`${index}-${step.title}`}>
                          <span>{index + 1}</span>
                          <div>
                            <strong>{step.title}</strong>
                            <p>{step.detail}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                    {plan.plan.questions.length ? (
                      <div className="agentPlanOpenQuestions">
                        <strong>Needs an answer</strong>
                        <ul>
                          {plan.plan.questions.map((question, index) => (
                            <li key={index}>{question}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    {plan.plan.assumptions.length ? (
                      <details className="agentPlanAssumptions">
                        <summary>
                          Assumptions ({plan.plan.assumptions.length}){' '}
                          <ChevronDown size={15} />
                        </summary>
                        <ul>
                          {plan.plan.assumptions.map((assumption, index) => (
                            <li key={index}>{assumption}</li>
                          ))}
                        </ul>
                      </details>
                    ) : null}
                    <footer>
                      <span>
                        This is a draft. No task, repository or runner was
                        changed.
                      </span>
                      <div>
                        <button
                          type="button"
                          className="agentTextButton"
                          onClick={() => void copyPlan(plan)}
                        >
                          <Copy size={14} /> Copy
                        </button>
                        {plan.status === 'draft' ? (
                          <>
                            <button
                              type="button"
                              className="agentTextButton"
                              onClick={() =>
                                void updatePlan(plan.agentWorkPlanId, 'stopped')
                              }
                            >
                              <X size={14} /> Stop
                            </button>
                            <button
                              type="button"
                              className="agentAcceptButton"
                              onClick={() =>
                                void updatePlan(
                                  plan.agentWorkPlanId,
                                  'accepted'
                                )
                              }
                            >
                              <Check size={14} /> Accept plan
                            </button>
                          </>
                        ) : null}
                      </div>
                    </footer>
                  </article>
                ))}
              </div>
            ) : (
              <div className="agentWorkEmpty agentWorkEmpty--quiet">
                <Sparkles size={22} />
                <h4>No plans yet</h4>
                <p>
                  Select a task to get a focused first draft, with open
                  questions called out before anyone picks up the work.
                </p>
              </div>
            )}
          </section>
        </div>
      ) : null}

      {!loading && tab === 'profiles' ? (
        <div className="agentProfilesLayout">
          <section className="agentProfilesList">
            <header className="agentWorkSectionHeading">
              <div>
                <div className="sectionEyebrow">SAVED ROLES</div>
                <h3>Profiles for different kinds of work</h3>
                <p>
                  Each profile defines its instructions, allowed task sources
                  and provider model.
                </p>
              </div>
              <button
                type="button"
                className="secondaryButton"
                onClick={() => beginProfile()}
              >
                <Plus size={16} /> New profile
              </button>
            </header>
            {profiles.length ? (
              profiles.map((profile) => (
                <button
                  type="button"
                  key={profile.agentProfileId}
                  className={`agentProfileRow${editing?.agentProfileId === profile.agentProfileId ? ' isSelected' : ''}`}
                  onClick={() => beginProfile(profile)}
                >
                  <span className="agentProfileIcon">
                    <Bot size={18} />
                  </span>
                  <span>
                    <strong>{profile.name}</strong>
                    <small>
                      {profile.role} · {profile.modelProvider} / {profile.model}
                    </small>
                    <small>
                      Sources:{' '}
                      {profile.taskProviders
                        .map((source) =>
                          source === 'clickup' ? 'ClickUp' : 'Plane'
                        )
                        .join(', ') || 'none'}
                    </small>
                  </span>
                  <span
                    className={`agentProfileState ${profile.status === 'active' ? 'isActive' : ''}`}
                  >
                    {profile.status === 'active' ? 'Active' : 'Disabled'}
                  </span>
                </button>
              ))
            ) : (
              <div className="agentWorkEmpty">
                <Bot size={24} />
                <h4>No agent profiles yet</h4>
                <p>
                  Create a role for a repeatable kind of work, such as
                  engineering planning, release review or marketing drafts.
                </p>
              </div>
            )}
          </section>
          {canManage ? (
            <form
              className="agentProfileEditor"
              onSubmit={(event) => void saveProfile(event)}
            >
              <header className="agentWorkSectionHeading">
                <div>
                  <div className="sectionEyebrow">
                    {editing ? 'EDIT PROFILE' : 'NEW PROFILE'}
                  </div>
                  <h3>{editing ? editing.name : 'Set up an agent role'}</h3>
                  <p>Only workspace admins can manage provider credentials.</p>
                </div>
              </header>
              <label>
                Name
                <input
                  required
                  minLength={2}
                  maxLength={120}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Engineering planner"
                />
              </label>
              <div className="agentProfileFieldGrid">
                <label>
                  Role
                  <select
                    value={role}
                    onChange={(event) =>
                      setRole(event.target.value as AgentProfileRow['role'])
                    }
                  >
                    <option value="planning">Planning</option>
                    <option value="engineering">Engineering</option>
                    <option value="marketing">Marketing</option>
                    <option value="review">Review</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>
                <label>
                  Task sources
                  <span className="agentProviderChecks">
                    {(['clickup', 'plane'] as const).map((source) => (
                      <span key={source}>
                        <input
                          id={`agent-source-${source}`}
                          type="checkbox"
                          checked={taskProviders.includes(source)}
                          onChange={(event) =>
                            setTaskProviders((current) =>
                              event.target.checked
                                ? [...current, source]
                                : current.filter((item) => item !== source)
                            )
                          }
                        />
                        <label htmlFor={`agent-source-${source}`}>
                          {source === 'clickup' ? 'ClickUp' : 'Plane'}
                        </label>
                      </span>
                    ))}
                  </span>
                </label>
              </div>
              <div className="agentProfileFieldGrid">
                <label>
                  Model provider
                  <select
                    value={provider}
                    onChange={(event) =>
                      setProvider(
                        event.target.value as AgentProfileRow['modelProvider']
                      )
                    }
                  >
                    <option value="openai">OpenAI</option>
                    <option value="anthropic">Anthropic</option>
                  </select>
                </label>
                <label>
                  Model ID
                  <input
                    required
                    maxLength={120}
                    value={model}
                    onChange={(event) => setModel(event.target.value)}
                    placeholder={
                      provider === 'openai'
                        ? 'Enter the OpenAI model ID'
                        : 'Enter the Anthropic model ID'
                    }
                  />
                </label>
              </div>
              <label>
                Provider API key
                <input
                  type="password"
                  autoComplete="new-password"
                  maxLength={500}
                  value={apiKey}
                  onChange={(event) => setApiKey(event.target.value)}
                  placeholder={
                    editing?.hasApiKey
                      ? 'Saved securely — enter a new key to replace it'
                      : 'Paste a provider API key'
                  }
                  required={!editing?.hasApiKey}
                />
              </label>
              <p className="agentCredentialNote">
                <KeyRound size={15} /> Encrypted in Cindro. The key is never
                shown again and is only used when a person asks for a plan.
              </p>
              <label>
                Instructions
                <textarea
                  required
                  minLength={10}
                  maxLength={6000}
                  rows={6}
                  value={instructions}
                  onChange={(event) => setInstructions(event.target.value)}
                />
              </label>
              {editing ? (
                <label className="agentToggleField">
                  <input
                    type="checkbox"
                    checked={editing.status === 'active'}
                    onChange={(event) =>
                      setEditing({
                        ...editing,
                        status: event.target.checked ? 'active' : 'disabled'
                      })
                    }
                  />{' '}
                  Profile can be selected for new plans
                </label>
              ) : null}
              <footer>
                <button
                  type="submit"
                  className="primaryButton"
                  disabled={busy || !taskProviders.length}
                >
                  {busy ? (
                    <LoaderCircle className="spin" size={16} />
                  ) : (
                    <Check size={16} />
                  )}{' '}
                  Save profile
                </button>
              </footer>
            </form>
          ) : (
            <div className="agentWorkEmpty">
              <KeyRound size={23} />
              <h4>Workspace admin access required</h4>
              <p>
                You can use existing profiles to plan work. Ask an admin to
                create or update profiles.
              </p>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
